import {
  AutoCandidateFit,
  AutoCorrelationReport,
  AutoParameterRecommendation,
  AutoValidationFlag,
  CalculationStrategies,
  SelectedFziFunctionsOptions
} from '../../@types/projects/types';
import { AutoCorrelationFamily } from '../../providers/AutoCorrelation';

export const TENDENCY_OPTIONS: SelectedFziFunctionsOptions[] = [
  'linear',
  'power',
  'logarithmic',
  'exponential'
];

export const TENDENCY_LABELS: Record<SelectedFziFunctionsOptions, string> = {
  linear: 'Linear',
  power: 'Power',
  logarithmic: 'Logarithmic',
  exponential: 'Exponential'
};

export interface ParameterFamilyGroup {
  key: AutoCorrelationFamily;
  label: string;
  parameters: string[];
}

/** Correlatable parameters per family (see FRONTEND.md). */
export const PARAMETER_FAMILIES: ParameterFamilyGroup[] = [
  {
    key: 'liao',
    label: 'Liao Pc',
    parameters: ['best_a', 'best_b', 'true_pc_max', 'swir', 'pce']
  },
  {
    key: 'lambda',
    label: 'Brooks-Corey Pc',
    parameters: ['pe', 'lambda_value']
  },
  {
    key: 'water_oil',
    label: 'Water-Oil Kr',
    parameters: ['sor_w', 'kro_absolute', 'krw_absolute', 'no_w', 'nw']
  },
  {
    key: 'gas_oil',
    label: 'Gas-Oil Kr',
    parameters: [
      'sor_g',
      'kro_absolute_g',
      'krg_absolute',
      'no_g',
      'ng',
      'sgc',
      'sor'
    ]
  }
];

export const FAMILY_LABELS: Record<AutoCorrelationFamily, string> = {
  liao: 'Liao Pc',
  lambda: 'Brooks-Corey Pc',
  water_oil: 'Water-Oil Kr',
  gas_oil: 'Gas-Oil Kr'
};

export const FAMILY_STRATEGY: Record<
  AutoCorrelationFamily,
  CalculationStrategies
> = {
  liao: CalculationStrategies.LIAO,
  lambda: CalculationStrategies.LAMBDA,
  water_oil: CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY,
  gas_oil: CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
};

export const FAMILY_KEYS: AutoCorrelationFamily[] = [
  'liao',
  'lambda',
  'water_oil',
  'gas_oil'
];

/**
 * Human-friendly parameter labels — kept identical to the Manual Correlation
 * chart labels (see design-system/correlation/components/Correlations/*),
 * so both workflows name the same variable the same way instead of showing
 * raw backend field names like "true_pc_max".
 */
export const PARAMETER_LABELS: Record<string, string> = {
  best_a: 'Best A',
  best_b: 'Best B',
  lambda_variable: 'Lambda',
  true_pc_max: 'Pc Max',
  swir: 'Swir',
  pce: 'PcE',
  pe: 'Pe',
  lambda_value: 'Brooks-Corey Exponent',
  sor_w: 'Sor',
  kro_absolute: 'Kro Absolute',
  krw_absolute: 'Krw Absolute',
  no_w: 'No',
  nw: 'Nw',
  sor_g: 'Sor',
  krg_absolute: 'Krg Absolute',
  no_g: 'Nog',
  ng: 'Ng',
  sgc: 'Sgc',
  sor: 'Sor'
};

export const parameterLabel = (parameter: string): string =>
  PARAMETER_LABELS[parameter] ?? parameter;

/** A candidate is rejected when a physical gate zeroed its score or a reject flag fired. */
export const isRejectedCandidate = (candidate: AutoCandidateFit): boolean =>
  candidate.score === null ||
  candidate.flags.some(flag => flag.severity === 'reject');

export const candidateWarnings = (
  candidate: AutoCandidateFit
): AutoValidationFlag[] =>
  candidate.flags.filter(flag => flag.severity === 'warning');

export const flagMessages = (flags: AutoValidationFlag[]): string =>
  flags.map(flag => `[${flag.severity}] ${flag.message}`).join('\n');

export const findCandidate = (
  parameter: AutoParameterRecommendation,
  type: SelectedFziFunctionsOptions | null | undefined
): AutoCandidateFit | undefined =>
  type
    ? parameter.candidates.find(candidate => candidate.tendency_type === type)
    : undefined;

/**
 * Warning-severity flags relevant to what apply() would persist: report-level
 * flags, per-parameter flags and the flags of each staged candidate.
 */
export const countStagedWarnings = (
  report: AutoCorrelationReport,
  stagedSelection: Record<string, SelectedFziFunctionsOptions>
): number => {
  let count = report.flags.filter(flag => flag.severity === 'warning').length;
  Object.values(report.parameters).forEach(parameter => {
    count += parameter.flags.filter(
      flag => flag.severity === 'warning'
    ).length;
    const staged = findCandidate(
      parameter,
      stagedSelection[parameter.parameter]
    );
    if (staged) count += candidateWarnings(staged).length;
  });
  return count;
};
